'use client';

import { cn } from 'v5/platform/dom/cn';
import {
  Popover,
  PopoverContent,
  PopoverAnchor,
  PopoverTrigger
} from './Popover';
import {
  InputGroup,
  InputGroupButton,
  InputGroupInput,
  type InputGroupVariant,
  type InputGroupSize
} from './InputGroup';
import {
  CalendarDate,
  type DateValue,
  createCalendar,
  getLocalTimeZone,
  isToday,
  maxDate,
  minDate
} from '@internationalized/date';
import {
  useRangeCalendar,
  useCalendarCell,
  useCalendarGrid
} from '@react-aria/calendar';
import { useDateField, useDateSegment } from '@react-aria/datepicker';
import { useFocusRing } from '@react-aria/focus';
import { AriaButton } from './_common/AriaButton';
import { useLocale } from '@react-aria/i18n';
import { useRangeCalendarState } from '@react-stately/calendar';
import {
  type DateFieldState,
  useDateFieldState
} from '@react-stately/datepicker';
import {
  ChevronLeft,
  ChevronRight,
  Calendar as CalendarIcon,
  ArrowRight
} from 'lucide-react';
import React, {
  ComponentProps,
  DOMAttributes,
  Fragment,
  useCallback,
  useMemo,
  useRef,
  useState
} from 'react';
import mergeRefs from 'merge-refs';
import { cva } from 'class-variance-authority';
import { useDismissableLayerFix } from 'v5/platform/react/useDismissableLayerFix';

// Custom DateSegment component using React Aria hooks
function DateSegment({
  segment,
  className,
  state,
  size,
  onOpenCalendar
}: {
  segment: Parameters<typeof useDateSegment>[0];
  className?: string;
  state: DateFieldState;
  size: InputGroupSize;
  onOpenCalendar?: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const { segmentProps } = useDateSegment(segment, state, ref);

  // Hide literal segments that are empty or only whitespace
  const isEmptyLiteral =
    segment.type === 'literal' && (!segment.text || segment.text.trim() === '');

  return (
    <div
      {...segmentProps}
      ref={ref}
      onKeyDown={e => {
        if (e.key === ' ') {
          e.preventDefault();
          onOpenCalendar?.();
          return;
        }
        segmentProps.onKeyDown?.(e);
      }}
      className={cn(
        className,
        segment.type === 'literal' && 'pointer-events-none px-1',
        segment.isPlaceholder && 'text-fg-muted italic',
        isEmptyLiteral && 'hidden'
      )}
      style={{
        ...segmentProps.style,
        minWidth:
          segment.maxValue != null
            ? String(segment.maxValue).length + 'ch'
            : undefined
      }}
    >
      {segment.isPlaceholder ? segment.placeholder : segment.text}
    </div>
  );
}

// Custom CalendarCell component for range selection
function RangeCalendarCell({
  date,
  className,
  state,
  monthOffset = 0
}: {
  date: CalendarDate;
  className?: (state: {
    date: CalendarDate;
    isSelected: boolean;
    isOutsideMonth: boolean;
    isDisabled: boolean;
    isUnavailable: boolean;
    isFocusVisible: boolean;
    isSelectionStart: boolean;
    isSelectionEnd: boolean;
  }) => string;
  state: ReturnType<typeof useRangeCalendarState>;
  monthOffset?: number;
}) {
  const ref = useRef<HTMLButtonElement>(null);
  const {
    cellProps,
    buttonProps,
    isSelected,
    isDisabled,
    isUnavailable,
    formattedDate
  } = useCalendarCell({ date }, state, ref);

  // Calculate if the date is outside the specific month we're displaying
  const targetMonth = state.visibleRange.start.add({ months: monthOffset });
  const isOutsideMonth =
    date.month !== targetMonth.month || date.year !== targetMonth.year;

  const { focusProps, isFocusVisible } = useFocusRing();

  // Get current range for start/end detection
  const getCurrentRange = () => {
    if ('highlightedRange' in state && state.highlightedRange) {
      return state.highlightedRange;
    }
    if (state.value && 'start' in state.value && 'end' in state.value) {
      return state.value;
    }
    return undefined;
  };

  const currentRange = getCurrentRange();

  const isSelectionStart = Boolean(
    isSelected &&
      currentRange &&
      currentRange.start &&
      date.compare(currentRange.start) === 0
  );

  const isSelectionEnd = Boolean(
    isSelected &&
      currentRange &&
      currentRange.end &&
      date.compare(currentRange.end) === 0
  );

  const cellState = {
    date,
    isSelected,
    isOutsideMonth,
    isDisabled,
    isUnavailable,
    isFocusVisible,
    isSelectionStart,
    isSelectionEnd
  };

  return (
    <div {...cellProps}>
      <button
        {...buttonProps}
        {...focusProps}
        ref={ref}
        className={className?.(cellState)}
      >
        {formattedDate}
      </button>
    </div>
  );
}

// Custom CalendarGrid component
function CalendarGrid({
  children,
  gridProps
}: {
  children: React.ReactNode;
  gridProps?: DOMAttributes<HTMLElement> & { className?: string };
}) {
  const { className, ...restProps } = gridProps || {};
  return (
    <div {...restProps} className={cn('w-full', className)}>
      {children}
    </div>
  );
}

// Custom CalendarGridHeader component
function CalendarGridHeader({
  children,
  headerProps,
  weekDays
}: {
  children: (day: string) => React.ReactNode;
  headerProps?: DOMAttributes<HTMLElement> & { className?: string };
  weekDays?: string[];
}) {
  const defaultWeekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const days = weekDays || defaultWeekDays;
  const { className, ...restProps } = headerProps || {};

  return (
    <div
      {...restProps}
      className={cn('grid grid-cols-7 gap-0 mb-1', className)}
    >
      {days.map(day => (
        <Fragment key={day}>{children(day)}</Fragment>
      ))}
    </div>
  );
}

// Custom CalendarGridBody component
function CalendarGridBody({ children }: { children: React.ReactNode }) {
  return <div className="grid grid-cols-7 gap-0">{children}</div>;
}

// Custom CalendarHeaderCell component
function CalendarHeaderCell({
  children,
  className
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <div className={className}>{children}</div>;
}

// Single month calendar grid component
function SingleMonthCalendarGrid({
  calendarState,
  monthOffset = 0,
  className
}: {
  calendarState: ReturnType<typeof useRangeCalendarState>;
  monthOffset?: number;
  className: (state: {
    date: CalendarDate;
    isSelected: boolean;
    isOutsideMonth: boolean;
    isDisabled: boolean;
    isUnavailable: boolean;
    isFocusVisible: boolean;
    isSelectionStart: boolean;
    isSelectionEnd: boolean;
  }) => string;
}) {
  const { gridProps, headerProps, weekDays, weeksInMonth } = useCalendarGrid(
    { weekdayStyle: 'short' },
    calendarState
  );

  const targetMonth = calendarState.visibleRange.start.add({
    months: monthOffset
  });

  return (
    <div className="flex flex-col">
      <CalendarGrid gridProps={gridProps}>
        <CalendarGridHeader headerProps={headerProps} weekDays={weekDays}>
          {day => (
            <CalendarHeaderCell className="text-fg-muted text-[0.8rem] font-normal text-center w-9 h-9 flex items-center justify-center">
              {day}
            </CalendarHeaderCell>
          )}
        </CalendarGridHeader>
        <CalendarGridBody>
          {Array.from({ length: weeksInMonth }, (_, weekIndex) =>
            calendarState
              .getDatesInWeek(weekIndex, targetMonth)
              .map((date, dayIndex) =>
                date ? (
                  <RangeCalendarCell
                    key={`${weekIndex}-${dayIndex}`}
                    date={date}
                    state={calendarState}
                    className={className}
                    monthOffset={monthOffset}
                  />
                ) : (
                  <div key={`${weekIndex}-${dayIndex}`} className="h-9 w-9" />
                )
              )
          )}
        </CalendarGridBody>
      </CalendarGrid>
    </div>
  );
}

const segmentVariants = cva(
  [
    'focus:outline-none focus:bg-brand-200/50',
    'font-variant-numeric:tabular-nums',
    'text-align:end'
  ],
  {
    variants: {
      size: {
        xs: 'text-sm rounded-sm px-0',
        sm: 'text-base py-0.5 rounded-sm px-0.5 -mx-0.25',
        md: 'text-base py-0.5 rounded-sm px-1 -mx-0.5',
        lg: 'text-lg py-1 rounded px-1 -mx-0.5',
        xl: 'text-xl py-2 rounded px-1 -mx-0.5'
      }
    }
  }
);

export interface DateRange {
  from: CalendarDate;
  to: CalendarDate;
}

/** A range whose ends may be missing; both missing is `null`. */
export interface PartialDateRange {
  from: CalendarDate | null;
  to: CalendarDate | null;
}

/** Naming and form wiring land on the group around both ends. */
export interface DateRangeInputProps
  extends Pick<
    ComponentProps<'div'>,
    'id' | 'aria-label' | 'aria-describedby' | 'aria-invalid'
  > {
  value?: PartialDateRange | null;
  /** Emits a partial range when one end is cleared, `null` when both are. */
  onChange?: (value: PartialDateRange | null) => void;
  size?: InputGroupSize;
  variant?: InputGroupVariant;
  className?: string;
  readOnly?: boolean;
  disabled?: boolean;
  minValue?: CalendarDate;
  maxValue?: CalendarDate;
  allowedRanges?: DateRange[];
}

const CALENDAR_VISIBLE_MONTHS = 2;

const pickMin = (dates: (CalendarDate | undefined)[]) =>
  dates.reduce<CalendarDate | undefined>(
    (acc, date) => minDate(acc, date) ?? undefined,
    undefined
  );

const pickMax = (dates: (CalendarDate | undefined)[]) =>
  dates.reduce<CalendarDate | undefined>(
    (acc, date) => maxDate(acc, date) ?? undefined,
    undefined
  );

const isInRange = (date: DateValue, range: DateRange) =>
  date.compare(range.from) >= 0 && date.compare(range.to) <= 0;

export const isCompleteDateRange = (
  range: PartialDateRange | null
): range is DateRange => range?.from != null && range.to != null;

/**
 * The range after one end is typed or cleared. Typing into an empty range sets
 * both ends; a typed start pushes an earlier end along with it, so typing a
 * year digit by digit never leaves the end behind.
 */
function withRangeEnd(
  range: PartialDateRange | null,
  end: 'from' | 'to',
  date: CalendarDate | null
): PartialDateRange | null {
  if (date === null) {
    const rest =
      end === 'from'
        ? { from: null, to: range?.to ?? null }
        : { from: range?.from ?? null, to: null };
    return rest.from === null && rest.to === null ? null : rest;
  }
  if (range === null) {
    return { from: date, to: date };
  }
  if (end === 'to') {
    return { from: range.from, to: date };
  }
  return {
    from: date,
    to: range.to !== null && range.to.compare(date) < 0 ? date : range.to
  };
}

/** Min/max for fields and calendar; allowed-day matching via isDateUnavailable. */
export function resolveDateRangeConstraints({
  minValue,
  maxValue,
  allowedRanges,
  value
}: {
  minValue?: CalendarDate;
  maxValue?: CalendarDate;
  allowedRanges?: DateRange[];
  value?: PartialDateRange | null;
}) {
  const ranges = (allowedRanges ?? []).map(range =>
    range.from.compare(range.to) <= 0
      ? range
      : { from: range.to, to: range.from }
  );

  const derivedMinValue = minValue ?? pickMin(ranges.map(range => range.from));
  const derivedMaxValue = maxValue ?? pickMax(ranges.map(range => range.to));

  const isDateUnavailable = (date: DateValue) => {
    if (!ranges.length) {
      return false;
    }
    const from = value?.from;
    const pendingChunk =
      from &&
      value.to?.compare(from) === 0 &&
      ranges.find(range => isInRange(from, range));
    const active = pendingChunk ? [pendingChunk] : ranges;
    return !active.some(range => isInRange(date, range));
  };

  return {
    derivedMinValue,
    derivedMaxValue,
    isDateUnavailable
  };
}

export const DateRangeInput = React.forwardRef<
  HTMLDivElement,
  DateRangeInputProps
>(
  (
    {
      value,
      onChange,
      size = 'md',
      variant = 'regular',
      className,
      readOnly,
      disabled,
      minValue,
      maxValue,
      allowedRanges,
      ...groupProps
    },
    ref
  ) => {
    const { derivedMinValue, derivedMaxValue, isDateUnavailable } = useMemo(
      () =>
        resolveDateRangeConstraints({
          minValue,
          maxValue,
          allowedRanges,
          value
        }),
      [minValue, maxValue, allowedRanges, value]
    );

    const { locale } = useLocale();
    const [open, setOpen] = useState(false);

    // Fix for Radix + React Aria focus issues
    const handleClose = useCallback(() => setOpen(false), []);
    useDismissableLayerFix(open, handleClose, [
      '[data-radix-popper-content-wrapper]'
    ]);

    // Start date field state
    const startFieldState = useDateFieldState({
      value: value?.from ?? null,
      onChange: date => onChange?.(withRangeEnd(value ?? null, 'from', date)),
      locale,
      createCalendar,
      isReadOnly: readOnly,
      isDisabled: disabled || readOnly,
      minValue: derivedMinValue,
      maxValue: derivedMaxValue,
      shouldForceLeadingZeros: true
    });

    // End date field state
    const endFieldState = useDateFieldState({
      value: value?.to ?? null,
      onChange: date => onChange?.(withRangeEnd(value ?? null, 'to', date)),
      locale,
      createCalendar,
      isReadOnly: readOnly,
      isDisabled: disabled || readOnly,
      minValue: derivedMinValue,
      maxValue: derivedMaxValue,
      shouldForceLeadingZeros: true
    });

    const startFieldRef = useRef<HTMLDivElement>(null);
    const endFieldRef = useRef<HTMLDivElement>(null);

    const { fieldProps: startFieldProps } = useDateField(
      { 'aria-label': 'Start date' },
      startFieldState,
      startFieldRef
    );

    const { fieldProps: endFieldProps } = useDateField(
      { 'aria-label': 'End date' },
      endFieldState,
      endFieldRef
    );

    // Range calendar state
    const rangeValue =
      value?.from && value?.to ? { start: value.from, end: value.to } : null;

    const calendarState = useRangeCalendarState({
      value: rangeValue,
      onChange: range => {
        if (range?.start && range?.end) {
          onChange?.({ from: range.start, to: range.end });
          // Keep popover open - user can close via click away or Enter
        }
      },
      visibleDuration: { months: CALENDAR_VISIBLE_MONTHS },
      locale,
      createCalendar,
      minValue: derivedMinValue,
      maxValue: derivedMaxValue,
      defaultFocusedValue: value?.from ?? value?.to ?? derivedMinValue,
      isDateUnavailable
    });

    const calendarRef = useRef<HTMLDivElement>(null);
    const { calendarProps, prevButtonProps, nextButtonProps } =
      useRangeCalendar(
        {
          'aria-label': 'Date range',
          autoFocus: true
        },
        calendarState,
        calendarRef
      );

    // Calendar cell styling for range
    const calendarCellClassName = (state: {
      date: CalendarDate;
      isSelected: boolean;
      isOutsideMonth: boolean;
      isDisabled: boolean;
      isUnavailable: boolean;
      isFocusVisible: boolean;
      isSelectionStart: boolean;
      isSelectionEnd: boolean;
    }) => {
      const isInactive = state.isDisabled || state.isUnavailable;
      const isRangeMiddle =
        state.isSelected && !state.isSelectionStart && !state.isSelectionEnd;
      const isRangeBoundary = state.isSelectionStart || state.isSelectionEnd;

      const baseClasses = cn(
        'h-9 w-9 text-center text-sm p-0 relative transition-colors',
        'flex items-center justify-center',
        'border-none',
        // Default state
        'text-fg-default',
        // Focus state
        state.isFocusVisible &&
          'outline-2 outline-brand-300 outline-offset-2 z-10'
      );

      const hoverClasses = cn(
        !isInactive && [
          // Default hover
          !state.isSelected && 'hover:bg-bg-subtle',
          // Range middle hover
          isRangeMiddle && 'hover:bg-brand-200',
          // Range boundary hover
          isRangeBoundary && 'hover:bg-brand-600'
        ]
      );

      const rangeClasses = cn(
        // Selected range middle
        isRangeMiddle && 'bg-brand-100 rounded-none',
        // Selection start
        state.isSelectionStart && 'bg-brand-500 text-white rounded-l-md',
        state.isSelectionStart && !state.isSelectionEnd && 'rounded-r-none',
        // Selection end
        state.isSelectionEnd && 'bg-brand-500 text-white rounded-r-md',
        state.isSelectionEnd && !state.isSelectionStart && 'rounded-l-none',
        // Both start and end (single day)
        state.isSelectionStart && state.isSelectionEnd && 'rounded-md'
      );

      const otherClasses = cn(
        // Today state - add a dot indicator
        isToday(state.date, getLocalTimeZone()) && [
          "relative after:content-[''] after:absolute after:bottom-1 after:left-1/2 after:-translate-x-1/2 after:w-1.5 after:h-1.5 after:rounded-full after:pointer-events-none",
          isRangeBoundary ? 'after:bg-white' : 'after:bg-brand-500'
        ],
        // Outside month state
        state.isOutsideMonth && 'text-fg-muted opacity-50',
        // Disabled / unavailable (e.g. gaps between allowedRanges)
        isInactive && 'text-fg-muted opacity-50 cursor-not-allowed'
      );

      return cn(baseClasses, hoverClasses, rangeClasses, otherClasses);
    };

    const containerRef = useRef<HTMLDivElement>(null);
    const mergedRef = mergeRefs(containerRef, ref);

    return (
      <Popover open={open} onOpenChange={setOpen} modal>
        <PopoverAnchor asChild>
          <InputGroup
            variant={variant}
            size={size}
            className={cn('overflow-hidden w-full *:w-fit ', className)}
            ref={mergedRef}
            role="group"
            {...groupProps}
            aria-readonly={readOnly}
            onDoubleClick={e => {
              if (disabled || readOnly) return;
              if (!open) {
                e.stopPropagation();
                setOpen(true);
              }
            }}
          >
            <InputGroupInput asChild>
              <div
                {...startFieldProps}
                className="flex items-center"
                ref={startFieldRef}
              >
                {startFieldState.segments.map((segment, i) => (
                  <DateSegment
                    key={i}
                    segment={segment}
                    state={startFieldState}
                    size={size}
                    className={segmentVariants({ size })}
                    onOpenCalendar={() => {
                      if (!disabled && !readOnly && !open) {
                        setOpen(true);
                      }
                    }}
                  />
                ))}
              </div>
            </InputGroupInput>
            <ArrowRight className="size-4 text-fg-muted shrink-0 self-center mx-2" />
            <InputGroupInput asChild>
              <div
                {...endFieldProps}
                ref={endFieldRef}
                className="flex items-center"
              >
                {endFieldState.segments.map((segment, i) => (
                  <DateSegment
                    key={i}
                    segment={segment}
                    state={endFieldState}
                    size={size}
                    className={segmentVariants({ size })}
                    onOpenCalendar={() => {
                      if (!disabled && !readOnly && !open) {
                        setOpen(true);
                      }
                    }}
                  />
                ))}
              </div>
            </InputGroupInput>
            {/* Read-only shows no calendar, as a read-only select shows no chevron. */}
            {!readOnly && (
              <PopoverTrigger asChild>
                <InputGroupButton
                  aria-expanded={open}
                  onClick={() => {
                    if (!disabled) {
                      setOpen(true);
                    }
                  }}
                  className="ml-auto"
                  tabIndex={-1}
                  disabled={disabled}
                >
                  <CalendarIcon />
                </InputGroupButton>
              </PopoverTrigger>
            )}
          </InputGroup>
        </PopoverAnchor>
        <PopoverContent
          className="p-3 w-auto min-w-0"
          align="start"
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault();
              setOpen(false);
            }
          }}
        >
          <div {...calendarProps} ref={calendarRef}>
            <header className="flex items-center mb-4">
              <AriaButton
                {...prevButtonProps}
                variant="ghost"
                size="sm"
                layout="icon"
                className="rounded p-1 shrink-0"
              >
                <ChevronLeft className="w-4 h-4" />
              </AriaButton>
              <div className="flex-1 text-center text-sm font-medium text-fg-default">
                {new Date(
                  calendarState.visibleRange.start.year,
                  calendarState.visibleRange.start.month - 1,
                  1
                ).toLocaleDateString(locale, {
                  month: 'long',
                  year: 'numeric'
                })}
              </div>
              <div className="w-24 shrink-0" />
              <div className="flex-1 text-center text-sm font-medium text-fg-default">
                {new Date(
                  calendarState.visibleRange.start.add({ months: 1 }).year,
                  calendarState.visibleRange.start.add({ months: 1 }).month - 1,
                  1
                ).toLocaleDateString(locale, {
                  month: 'long',
                  year: 'numeric'
                })}
              </div>
              <AriaButton
                {...nextButtonProps}
                variant="ghost"
                size="sm"
                layout="icon"
                className="rounded p-1 shrink-0"
              >
                <ChevronRight className="w-4 h-4" />
              </AriaButton>
            </header>
            <div className="flex gap-6">
              <SingleMonthCalendarGrid
                calendarState={calendarState}
                monthOffset={0}
                className={calendarCellClassName}
              />
              <SingleMonthCalendarGrid
                calendarState={calendarState}
                monthOffset={1}
                className={calendarCellClassName}
              />
            </div>
          </div>
        </PopoverContent>
      </Popover>
    );
  }
);

DateRangeInput.displayName = 'DateRangeInput';
